import { FieldCondition } from '@common/types/api';

/**
 * Normalize a field value for comparison.
 * Handles multiselect (array of {value, label} objects),
 * single select (string), and other types.
 */
const normalizeValue = (value: unknown): string | string[] => {
  if (value === null || value === undefined) return '';

  // Array of objects (multiselect): extract .value from each
  if (Array.isArray(value)) {
    return value.map((item) =>
      typeof item === 'object' && item !== null && 'value' in item
        ? String((item as { value: unknown }).value)
        : String(item),
    );
  }

  // Single object with .value (shouldn't happen but handle it)
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return String((value as { value: unknown }).value);
  }

  return String(value);
};

export const evaluateCondition = (
  condition: FieldCondition,
  watchedValues: Record<string, unknown>,
): boolean => {
  const rawValue = watchedValues[condition.field];
  const fieldValue = normalizeValue(rawValue);
  const conditionValue = String(condition.value);

  switch (condition.operator) {
    case 'equals':
      if (Array.isArray(fieldValue)) {
        return fieldValue.some((v) => v === conditionValue);
      }
      return fieldValue === conditionValue;

    case 'not_equals':
      if (Array.isArray(fieldValue)) {
        return !fieldValue.some((v) => v === conditionValue);
      }
      return fieldValue !== conditionValue;

    case 'contains':
      if (Array.isArray(fieldValue)) {
        return fieldValue.some((v) => v.includes(conditionValue));
      }
      return fieldValue.includes(conditionValue);

    case 'not_contains':
      if (Array.isArray(fieldValue)) {
        return !fieldValue.some((v) => v.includes(conditionValue));
      }
      return !fieldValue.includes(conditionValue);

    case 'greater':
      return Number(fieldValue) > Number(conditionValue);

    case 'less':
      return Number(fieldValue) < Number(conditionValue);

    case 'empty':
      return (
        rawValue === undefined ||
        rawValue === null ||
        rawValue === '' ||
        (Array.isArray(rawValue) && rawValue.length === 0)
      );

    case 'not_empty':
      return (
        rawValue !== undefined &&
        rawValue !== null &&
        rawValue !== '' &&
        !(Array.isArray(rawValue) && rawValue.length === 0)
      );

    default:
      return true;
  }
};

export const evaluateConditions = (
  conditions: FieldCondition[] | undefined,
  watchedValues: Record<string, unknown>,
): boolean => {
  if (!conditions || conditions.length === 0) {
    return true;
  }

  // All conditions must be true (AND logic)
  return conditions.every((condition) =>
    evaluateCondition(condition, watchedValues),
  );
};
